/**
 * Compliance-velocity dashboard — Phase 7F.3.
 * Admin-key gated. On-demand refresh (no polling — see 7F.6 for contention rationale).
 */
import React, { useEffect, useState } from 'react'
import { AdminThemeToggle, useAdminTheme } from './admin/theme'
import AnomalyBanner from './admin/AnomalyBanner'

const API_URL = process.env.REACT_APP_BACKEND_URL || ''
const NAVY = 'var(--admin-navy)', GOLD = 'var(--admin-gold)', IVORY = 'var(--admin-ivory)', SLATE = 'var(--admin-slate)', MUTED = 'var(--admin-muted)', DANGER = 'var(--admin-danger)'
const SURFACE = 'var(--admin-surface)', BORDER = 'var(--admin-border)', BORDER_SOFT = 'var(--admin-border-soft)', SCRIM = 'var(--admin-scrim)'
const NAVY_SOLID = 'var(--admin-navy-solid)', IVORY_SOLID = 'var(--admin-ivory-solid)'

function useAdminKey() {
  const [key, setKey] = useState(() => localStorage.getItem('lwp_admin_key') || '')
  const [input, setInput] = useState('')
  return { key, setKey: (v) => { localStorage.setItem('lwp_admin_key', v); setKey(v) }, input, setInput, reset: () => { localStorage.removeItem('lwp_admin_key'); setKey('') } }
}

export default function AdminCompliance() {
  useAdminTheme()
  const auth = useAdminKey()
  const [window, setWindow] = useState('30d')
  const [data, setData] = useState(null)
  const [err, setErr] = useState(null)
  const [loading, setLoading] = useState(false)

  const load = () => {
    if (!auth.key) return
    setLoading(true); setErr(null)
    fetch(`${API_URL}/api/analytics/compliance-velocity?admin_key=${encodeURIComponent(auth.key)}&window=${window}`)
      .then(r => { if (r.status === 401) throw new Error('Invalid admin key'); if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json() })
      .then(setData).catch(e => setErr(e.message)).finally(() => setLoading(false))
  }
  useEffect(load, [auth.key, window])

  if (!auth.key) return <KeyGate auth={auth} title="Compliance velocity" />
  if (loading) return <Loading />
  if (err) return <ErrView err={err} reset={auth.reset} />
  if (!data) return null

  const maxSpark = Math.max(1, ...data.daily_sparkline.map(d => d.rejections + d.overrides))

  return (
    <div style={{ minHeight: '100vh', background: IVORY, fontFamily: 'Inter,-apple-system,sans-serif', padding: 24, color: SLATE }} data-testid="compliance-dashboard">
      <AnomalyBanner adminKey={auth.key} />
      <Header title="Compliance velocity" subtitle={`${data.audit_total_entries} total audit entries · window: ${data.window}`}>
        <WindowSwitch value={window} onChange={setWindow} options={['30d', '90d']} />
        <AdminThemeToggle />
        <button onClick={load} style={btn} data-testid="refresh-btn">Refresh</button>
      </Header>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
        <Stat label="Rejected saves" value={data.rejected_saves} accent={DANGER} />
        <Stat label="Overrides used" value={data.overrides} accent={GOLD} />
        <Stat label="Softening events" value={data.softening_events} accent={NAVY} />
        <Stat label="License assignments" value={data.license_assignments} accent={NAVY} />
      </div>
      <Card title="Rejections + overrides — daily sparkline">
        <div style={{ display: 'flex', alignItems: 'flex-end', height: 100, gap: 3 }} data-testid="compliance-sparkline">
          {data.daily_sparkline.map(d => (
            <div key={d.date} title={`${d.date}: ${d.rejections} rej / ${d.overrides} ovr`} style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 1, minHeight: 4 }}>
              <div style={{ background: DANGER, height: `${(d.rejections/maxSpark)*70}%`, minHeight: d.rejections ? 2 : 0 }} />
              <div style={{ background: GOLD, height: `${(d.overrides/maxSpark)*70}%`, minHeight: d.overrides ? 2 : 0 }} />
            </div>
          ))}
        </div>
        <div style={{ fontSize: 11, color: MUTED, marginTop: 6 }}>Red = rejections · Gold = overrides · Hover for date/counts</div>
      </Card>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
        <Card title="Rejections by category">
          <table style={tbl}>
            <tbody>
              {Object.entries(data.rejected_by_category || {}).map(([k, v]) => (
                <tr key={k}><td style={tdL}>{k}</td><td style={tdR}>{v}</td></tr>
              ))}
              {!Object.keys(data.rejected_by_category || {}).length && <tr><td colSpan={2} style={{...tdL, color:MUTED}}>None this window.</td></tr>}
            </tbody>
          </table>
        </Card>
        <Card title="Top offenders by actor">
          <table style={tbl}>
            <tbody>
              {(data.top_offenders_by_actor || []).map(o => (
                <tr key={o.actor_email}><td style={tdL}>{o.actor_email}</td><td style={tdR}>{o.count}</td></tr>
              ))}
              {!(data.top_offenders_by_actor || []).length && <tr><td colSpan={2} style={{...tdL, color:MUTED}}>Clean board.</td></tr>}
            </tbody>
          </table>
        </Card>
      </div>
      <Card title="Recent softening events (compliance-snippet softening)">
        <table style={tbl}>
          <thead><tr><th style={thL}>Snippet</th><th style={thR}>Reduction %</th><th style={thL}>Actor</th></tr></thead>
          <tbody>
            {(data.softening_details || []).map((s,i) => (
              <tr key={i}><td style={tdL}>{s.snippet_key}</td><td style={tdR}>{s.reduction_pct}%</td><td style={tdL}>{s.actor}</td></tr>
            ))}
            {!(data.softening_details || []).length && <tr><td colSpan={3} style={{...tdL, color:MUTED}}>No softening events.</td></tr>}
          </tbody>
        </table>
      </Card>
      <div style={{ marginTop: 16 }}>
        <a href="/admin/collections/compliance-audit" style={{ ...btn, textDecoration: 'none' }} data-testid="open-audit-drilldown">Open full audit log →</a>
      </div>
    </div>
  )
}

// ---- shared UI helpers ----
const btn = { padding: '8px 14px', background: NAVY_SOLID, color: IVORY_SOLID, border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 500, cursor: 'pointer' }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 13 }
const thL = { textAlign: 'left', padding: 6, borderBottom: `1px solid ${BORDER}`, color: MUTED, fontWeight: 500 }
const thR = { ...thL, textAlign: 'right' }
const tdL = { padding: 6, borderBottom: `1px solid ${BORDER_SOFT}` }
const tdR = { ...tdL, textAlign: 'right', fontWeight: 500 }
export function Stat({ label, value, accent }) {
  return <div style={{ background: SURFACE, padding: 16, borderRadius: 8, borderLeft: `3px solid ${accent}` }}>
    <div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
    <div style={{ fontSize: 28, fontFamily: 'Fraunces,serif', color: NAVY, marginTop: 4 }}>{value}</div>
  </div>
}
export function Card({ title, children }) {
  return <div style={{ background: SURFACE, padding: 16, borderRadius: 8, marginTop: 12 }}>
    <div style={{ fontSize: 12, fontWeight: 600, color: SLATE, marginBottom: 8 }}>{title}</div>{children}</div>
}
export function Header({ title, subtitle, children }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
    <div>
      <h1 style={{ fontFamily: 'Fraunces,serif', color: NAVY, margin: 0, fontSize: 30 }}>{title}</h1>
      <div style={{ color: MUTED, fontSize: 12, marginTop: 4 }}>{subtitle}</div>
    </div>
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>{children}</div>
  </div>
}
export function WindowSwitch({ value, onChange, options }) {
  return <div style={{ display: 'flex', background: SURFACE, borderRadius: 6, padding: 2, border: `1px solid ${BORDER}` }}>
    {options.map(o => (
      <button key={o} onClick={() => onChange(o)} style={{ padding: '6px 12px', background: value === o ? NAVY_SOLID : 'transparent', color: value === o ? IVORY_SOLID : SLATE, border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer' }}>{o}</button>
    ))}
  </div>
}
export function KeyGate({ auth, title }) {
  return <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: IVORY, fontFamily: 'Inter,sans-serif', color: SLATE }} data-testid="auth-gate">
    <div style={{ maxWidth: 420, width: '100%', padding: 24 }}>
      <h1 style={{ fontFamily: 'Fraunces,serif', color: NAVY, margin: '0 0 8px' }}>{title}</h1>
      <p style={{ color: MUTED, fontSize: 14, marginBottom: 20 }}>Admin only. Enter ADMIN_API_KEY.</p>
      <input type="password" value={auth.input} onChange={e => auth.setInput(e.target.value)} placeholder="ADMIN_API_KEY" style={{ width: '100%', padding: '12px 16px', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 14, marginBottom: 10, background: SURFACE, color: SLATE }} data-testid="key-input" />
      <button onClick={() => auth.setKey(auth.input)} style={{ ...btn, width: '100%' }} data-testid="key-submit">Continue</button>
    </div>
  </div>
}
export function Loading() { return <div style={{ padding: 40, fontFamily: 'Inter,sans-serif', background: IVORY, minHeight: '100vh', color: SLATE }} data-testid="admin-loading">Loading…</div> }
export function ErrView({ err, reset }) { return <div style={{ padding: 40, fontFamily: 'Inter,sans-serif', color: DANGER, background: IVORY, minHeight: '100vh' }} data-testid="admin-error"><h2>Error</h2><p>{err}</p><button onClick={reset} style={btn}>Change admin key</button></div> }
